import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { InviteePromptDetail, InviteeQuestion } from '../../../../core/models/schedule-prompt.models';

@Component({
  selector: 'app-invitee-view',
  imports: [RouterLink, NgClass],
  templateUrl: './invitee-view.html',
  styleUrl: './invitee-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteeView implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api   = inject(ApiService);

  readonly schedulePromptId = signal<string | null>(null);
  readonly hasError         = signal(false);
  readonly detail           = signal<InviteePromptDetail | null>(null);
  readonly activeFilter     = signal<'all' | 'responded' | 'not-responded' | 'waiting'>('all');

  ngOnInit(): void {
    this.schedulePromptId.set(this.route.snapshot.paramMap.get('schedulePromptId'));
    this.loadDetail();
  }

  async loadDetail(): Promise<void> {
    const id = this.schedulePromptId();
    if (!id) return;
    this.hasError.set(false);
    try {
      const resp = await this.api.request<InviteePromptDetail>(
        'GET',
        `${API_ROUTES.SP_GET_INVITEE_DETAIL}/${id}`,
        undefined,
        { showToaster: false, showLoader: true, useToken: true },
      );
      if (resp.status && resp.data) {
        this.detail.set(resp.data);
      } else {
        this.hasError.set(true);
      }
    } catch {
      this.hasError.set(true);
    }
  }

  /** Returns initials from a full name, e.g. "Robert King" → "RK". */
  getInitials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  /** CSS state class for the Current Prompt card. */
  getCurrentPromptCardClass(): string {
    const q = this.detail()?.currentQuestion;
    if (!q) return '';
    if (q.hasResponded)  return 'fm-current-responded';
    if (q.isExpired)     return 'fm-current-not-responded';
    return 'fm-current-awaiting';
  }

  /** CSS modifier class for history item wrapper. */
  getHistoryItemClass(q: InviteeQuestion): string {
    if (q.hasResponded)  return 'fm-responded';
    if (q.isExpired)     return 'fm-not-responded';
    return 'fm-waiting';
  }

  /** CSS badge class. */
  getStatusBadgeClass(q: InviteeQuestion): string {
    if (q.hasResponded)  return 'fm-status-success';
    if (q.isExpired)     return 'fm-status-danger';
    return 'fm-status-warning';
  }

  /** Bootstrap icon for the timeline dot. */
  getTimelineIcon(q: InviteeQuestion): string {
    if (q.hasResponded)  return 'bi-check-lg';
    if (q.isExpired)     return 'bi-x-lg';
    return 'bi-hourglass-split';
  }

  /** Human-readable status label. */
  getStatusLabel(q: InviteeQuestion): string {
    if (q.hasResponded)  return 'Responded';
    if (q.isExpired)     return 'No Response Received';
    return 'Awaiting Response';
  }

  /** CSS badge class for the cycle status string from the API. */
  getCycleStatusClass(cycleStatus: string): string {
    switch (cycleStatus?.toLowerCase()) {
      case 'responded':                return 'fm-status-success';
      case 'expired':
      case 'no response received':     return 'fm-status-danger';
      default:                         return 'fm-status-warning';
    }
  }

  /** Bootstrap icon for the cycle status badge. */
  getCycleStatusIcon(cycleStatus: string): string {
    switch (cycleStatus?.toLowerCase()) {
      case 'responded':                return 'bi-check-circle-fill';
      case 'expired':
      case 'no response received':     return 'bi-x-circle-fill';
      default:                         return 'bi-hourglass-split';
    }
  }

  /** Filtered previous questions for the history tab. */
  filteredHistory(): InviteeQuestion[] {
    const all = this.detail()?.previousQuestions ?? [];
    switch (this.activeFilter()) {
      case 'responded':     return all.filter((q) => q.hasResponded);
      case 'not-responded': return all.filter((q) => q.isExpired && !q.hasResponded);
      case 'waiting':       return all.filter((q) => !q.hasResponded && !q.isExpired);
      default:              return all;
    }
  }

  setFilter(f: 'all' | 'responded' | 'not-responded' | 'waiting'): void {
    this.activeFilter.set(f);
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
}
